import { ref, onMounted } from "vue";
import { useConfig } from "./useConfig";
import axios from "axios";
const today = new Date();
export function useMagnetogramViewer() {
    // reactive state
    const imageUrl = ref(null);
    const imageUrl2 = ref(null);
    const imageUrl3 = ref(null);
    const imageUrl4 = ref(null);
    const imageUrls = [imageUrl, imageUrl2, imageUrl3, imageUrl4];
    const selectedYear = ref(today.getFullYear());
    const selectedMonth = ref(today.getMonth() + 1);
    const selectedDay = ref(today.getDate());
    const appConfig = useConfig();
    const view = ref(appConfig.timeInterval ?? "datenDay");
    const dataBaseUrl = `${appConfig.baseUrl}/`;
    const labelSelectYear = appConfig.text?.labelSelectYear;
    const labelSelectMonth = appConfig.text?.labelSelectMonth;
    const labelSelectDay = appConfig.text?.labelSelectDay;
    const labelButtonNow = appConfig.text?.labelButtonNow;
    const labelButtonPrevious = appConfig.text?.labelButtonPrevious;
    const labelButtonNext = appConfig.text?.labelButtonNext;
    const textNoImagesAvailable = appConfig.text?.textNoImagesAvailable;
    const years = Array.from({ length: new Date().getFullYear() - 2001 + 1 }, (_, i) => 2001 + i);
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const days = Array.from({ length: 31 }, (_, i) => i + 1);
    // builds the image file path according to the active view.
    const getImageFilePath = () => {
        const year = selectedYear.value;
        const month = String(selectedMonth.value).padStart(2, "0"); // Monat auf 2 Ziffern formatieren
        const day = String(selectedDay.value).padStart(2, "0"); // Tag auf 2 Ziffern formatieren
        let imageFilePrefix;
        if (view.value === "datenDay") {
            imageFilePrefix = `datenDay/${year}/FUR${year}${month}${day}`;
        }
        else if (view.value === "datenMonth") {
            imageFilePrefix = `datenMonth/${year}/${year}${month}`;
        }
        else if (view.value === "datenYear") {
            imageFilePrefix = `datenYear/${year}`;
        }
        return dataBaseUrl + imageFilePrefix;
    };
    /* Loading images based on the current view
    datenDay: always one image is loaded
    datenMonth, datenYear: maximum of four images are loaded. File names also have a flag (suffix)
    */
    const loadImagesBasedOnView = async () => {
        const fileNameFlags = {
            datenDay: [""],
            datenMonth: ["_F", "_X", "_Y", "_Z"],
            datenYear: ["_D", "_F", "_H", "_Z"]
        };
        // Get filename flags for selected view
        const flags = fileNameFlags[view.value ?? 'datenDay'];
        // number of images corresponds to the number of filename flags.
        const numberOfImages = flags.length;
        // Determines the file path for each image and load each image.
        for (let i = 0; i < numberOfImages; i++) {
            const imageFilePath = getImageFilePath() + flags[i] + ".JPG";
            imageUrls[i].value = await loadImage(imageFilePath);
        }
    };
    // Loads the image from the server via an HTTP GET request and returns a URL to display it.
    const loadImage = async (imageFile) => {
        if (!selectedYear.value ||
            (view.value === "datenDay" &&
                (!selectedMonth.value || !selectedDay.value))) {
            return null; // Bild nicht laden, wenn nicht alle Werte ausgewählt sind
        }
        try {
            const response = await axios.get(imageFile, {
                responseType: "blob"
            });
            const blobUrl = URL.createObjectURL(response.data);
            return blobUrl; // Bild-URL zurückgeben
        }
        catch (error) {
            // console.error('Error loading image:', error);
            return null; // Bild-URL zurückgeben, wenn ein Fehler auftritt
        }
    };
    // checks for a valid date
    const isDateValid = (year, month, day) => {
        const date = new Date(year, month - 1, day); // Monat ist 0-indexiert
        return (date.getFullYear() === year &&
            date.getMonth() + 1 === month &&
            date.getDate() === day);
    };
    // determines the next valid date for an invalid date
    const getNextValidDate = (year, month, day) => {
        const date = new Date(year, month - 1, day);
        while (!isDateValid(date.getFullYear(), date.getMonth() + 1, date.getDate())) {
            date.setDate(date.getDate() + 1); // Nächstes Datum
        }
        return {
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate()
        };
    };
    // Loads the images associated with this date after changing the selection
    const onDropdownChange = async () => {
        let newYear = selectedYear.value;
        let newMonth = selectedMonth.value;
        let newDay = selectedDay.value;
        // Check whether the date is valid when the view is 'datenDay'
        if (view.value === "datenDay" &&
            !isDateValid(newYear, newMonth, newDay)) {
            // If invalid, find next valid date
            const nextDate = getNextValidDate(newYear, newMonth, newDay);
            selectedYear.value = nextDate.year;
            selectedMonth.value = nextDate.month;
            selectedDay.value = nextDate.day;
        }
        loadImagesBasedOnView();
    };
    // Loads images for the previous date
    const prevDate = async () => {
        if (view.value === "datenDay") {
            const newDate = new Date(selectedYear.value, selectedMonth.value - 1, selectedDay.value);
            newDate.setDate(newDate.getDate() - 1); // Gestern
            selectedYear.value = newDate.getFullYear();
            selectedMonth.value = newDate.getMonth() + 1; // Monat ist 0-indexiert
            selectedDay.value = newDate.getDate();
        }
        else if (view.value === "datenMonth") {
            const newDate = new Date(selectedYear.value, selectedMonth.value - 1);
            newDate.setMonth(newDate.getMonth() - 1); // Vorheriger Monat
            selectedYear.value = newDate.getFullYear();
            selectedMonth.value = newDate.getMonth() + 1; // Monat ist 0-indexiert
        }
        else if (view.value === "datenYear") {
            selectedYear.value -= 1; // Vorheriges Jahr
        }
        loadImagesBasedOnView();
    };
    // Loads images for today's date
    const nowDate = async () => {
        selectedYear.value = today.getFullYear();
        selectedMonth.value = today.getMonth() + 1; // Monate sind 0-indexiert
        selectedDay.value = today.getDate();
        loadImagesBasedOnView();
    };
    // Loads images for the previous date
    const nextDate = async () => {
        if (view.value === "datenDay") {
            const newDate = new Date(selectedYear.value, selectedMonth.value - 1, selectedDay.value);
            newDate.setDate(newDate.getDate() + 1); // Morgen
            selectedYear.value = newDate.getFullYear();
            selectedMonth.value = newDate.getMonth() + 1; // Monat ist 0-indexiert
            selectedDay.value = newDate.getDate();
        }
        else if (view.value === "datenMonth") {
            const newDate = new Date(selectedYear.value, selectedMonth.value - 1);
            newDate.setMonth(newDate.getMonth() + 1); // Nächster Monat
            selectedYear.value = newDate.getFullYear();
            selectedMonth.value = newDate.getMonth() + 1; // Monat ist 0-indexiert
        }
        else if (view.value === "datenYear") {
            selectedYear.value += 1; // Nächstes Jahr
        }
        loadImagesBasedOnView();
    };
    onMounted(async () => {
        console.info("Load initial image(s) according to FS-template param timeIntervall: ", view.value);
        loadImagesBasedOnView();
    });
    return {
        nextDate,
        nowDate,
        prevDate,
        onDropdownChange,
        years,
        months,
        days,
        selectedDay,
        selectedMonth,
        selectedYear,
        labelSelectYear,
        labelSelectMonth,
        labelSelectDay,
        labelButtonNow,
        labelButtonPrevious,
        labelButtonNext,
        textNoImagesAvailable,
        imageUrls,
        view
    };
}
